import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { listSiteContractSettingsQuery } from '@shared/services/gql.service';
import {
  decimalAdd,
  decimalDifference,
  decimalDivide,
  decimalMultiply,
  decimalRoundingToNumber,
} from '@shared/utils';

import {
  ContractedInvestigatorAmountValues,
  SiteContractAveragesValues,
} from '../types/investigator-forecast.types';

@Injectable({ providedIn: 'root' })
export class InvestigatorAveragesService {
  editMode$ = new BehaviorSubject<boolean>(false);

  private maxPercent = 100;

  // invoiceablesPerc - from 0 to 100%
  getForecastedInvoiceables(
    forecastedVisitCosts: number,
    invoiceablesPerc?: null | number
  ): number {
    return decimalRoundingToNumber(
      decimalMultiply(forecastedVisitCosts, (invoiceablesPerc || 0) / this.maxPercent)
    );
  }

  getForecastedVisitCost(totalForecastedPatients: number, averageVisitCosts: number): number {
    return decimalMultiply(totalForecastedPatients, averageVisitCosts, 2);
  }

  getInitialSummaryValues(
    averagesValues: listSiteContractSettingsQuery[],
    contractedValues: ContractedInvestigatorAmountValues
  ): SiteContractAveragesValues {
    const averages = averagesValues[0]; // last updated values

    const totalForecastedPatients = averages.total_forecasted_patients;
    const averagePatientCost = decimalRoundingToNumber(averages.average_patient_cost);

    const forecastedVisitCosts = this.getForecastedVisitCost(
      totalForecastedPatients,
      averagePatientCost
    );

    const forecastedInvoiceables = this.getForecastedInvoiceables(
      forecastedVisitCosts,
      averages.invoiceables_percent
    );

    const totalInvestigatorCosts = this.getTotalInvestigatorCost(
      forecastedVisitCosts,
      forecastedInvoiceables
    );

    const remainingAmount = this.getRemainingAmount(
      totalInvestigatorCosts,
      contractedValues.spendToDateAmount
    );

    const defaultOtherInvoiceablesPerc = decimalDivide(
      contractedValues.otherInvoiceables * this.maxPercent,
      contractedValues.totalInvoiceables
    );

    const defaultOverheadInvoiceablesPerc = decimalDifference(
      this.maxPercent,
      defaultOtherInvoiceablesPerc
    );

    return {
      averagePatientCost,
      contractedSites: averages.last_updated,
      forecastedInvoiceables,
      forecastedVisitCosts,
      invoiceables: averages.invoiceables_percent || 0,
      otherInvoiceablesPerc: decimalRoundingToNumber(
        averages.other_percent || defaultOtherInvoiceablesPerc
      ),
      overheadInvoiceablesPerc: decimalRoundingToNumber(
        averages.overhead_percent || defaultOverheadInvoiceablesPerc
      ),
      remainingAmount,
      spendToDateAmount: contractedValues.spendToDateAmount,
      totalForecastedPatients,
      totalInvestigatorCosts,
    };
  }

  getRemainingAmount(investigatorCost: number, spendToDateAmount: number): number {
    return decimalDifference(investigatorCost, spendToDateAmount);
  }

  getTotalInvestigatorCost(forecastedVisitCosts: number, forecastedInvoiceables: number): number {
    return decimalAdd(forecastedVisitCosts, forecastedInvoiceables);
  }
}
